/* eslint-disable sonarjs/no-duplicate-string */
export function toPositiveValue(value?: number): number | undefined {
  return value ? Math.abs(value) : undefined;
}

export function toStringNumberWithDelimiter(value?: number, delimiter = ','): string | undefined {
  return value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
}

export const isValidNumber = (value: number, sign = 1): boolean =>
  typeof value === 'number' && Math.sign(value) >= sign;

export const round = (value: number, decimalPlaces = 2): number =>
  Number(value.toFixed(decimalPlaces));

export const percentage = {
  from: (value: number, total: number, decimalPlaces = 0): number => {
    try {
      if (isValidNumber(value, 0) && isValidNumber(total, 0)) {
        return value > 0 && total > 0 ? round(total * (value / 100), decimalPlaces) : 0;
      }

      throw new Error(
        `Invalid number provided to percentage-from function. Values: ${value}, ${total}`,
      );
    } catch (e) {
      console.error(e);
      return NaN;
    }
  },
  of: (value: number, total: number, decimalPlaces = 0): number => {
    try {
      if (isValidNumber(value, 0) && isValidNumber(total, 0)) {
        return value > 0 && total > 0 ? round((value / total) * 100, decimalPlaces) : 0;
      }

      throw new Error(
        `Invalid number provided to percentage-of function. Values: ${value}, ${total}`,
      );
    } catch (e) {
      console.error(e);
      return NaN;
    }
  },
  change: (value1: number, value2: number, decimalPlaces = 2): number => {
    try {
      if (isValidNumber(value1, 0) && isValidNumber(value2, 0)) {
        return value1 > 0 && value2 >= 0
          ? round(((value2 - value1) / value1) * 100, decimalPlaces)
          : 0;
      }

      throw new Error(
        `Invalid number provided to percentage-change function. Values: ${value1}, ${value2}`,
      );
    } catch (e) {
      console.error(e);
      return NaN;
    }
  },
};

export const conversionRate = (
  conversions: number,
  visitors: number,
  decimalPlaces = 2,
): number => {
  try {
    if (isValidNumber(conversions, 0) && isValidNumber(visitors, 0)) {
      const result =
        conversions > 0 && visitors > 0 ? round((conversions / visitors) * 100, decimalPlaces) : 0;
      return result === Infinity ? 0 : result;
    }

    throw new Error(
      `Invalid number provided to conversionRate function. Values: ${conversions}, ${visitors}`,
    );
  } catch (e) {
    console.error(e);
    return NaN;
  }
};

export const roundToTwoDecimals = (positiveNumber: number): number => {
  if (positiveNumber === 0) {
    return 0;
  }
  // Number.EPSILON used to avoid rounding errors in our beloved JS
  // https://stackoverflow.com/a/77281645
  return Math.round((positiveNumber + Number.EPSILON) * 100) / 100;
};

/** Curly apostrophe (’), not ASCII `'`. Spec: ASR-1648 / de_CH. */
const UBM_THOUSAND_SEPARATOR_DE = '\u2019';
/** Non-breaking space. Spec: ASR-1648 / fr_CH & it_CH. */
const UBM_THOUSAND_SEPARATOR_FR_IT = '\u00A0';

/**
 * Thousand separators for UBM currency-style amounts (IC, visibility upgrades).
 * Listing card / Domus `hgPrice` formatting is intentionally separate.
 *
 * de: 1’234.00 · en: 1,234.00 · fr/it: 1 234.00 (NBSP)
 */
const getUbmThousandSeparator = (locale: string): string => {
  const language = locale.toLowerCase().split('-')[0];

  switch (language) {
    case 'en':
      return ',';
    case 'fr':
    case 'it':
      return UBM_THOUSAND_SEPARATOR_FR_IT;
    case 'de':
    default:
      return UBM_THOUSAND_SEPARATOR_DE;
  }
};

/** Groups an unsigned integer digit string from the right in threes (no regex). */
const groupIntegerDigits = (integerDigits: string, thousandSeparator: string): string => {
  const groups: string[] = [];

  for (let end = integerDigits.length; end > 0; end -= 3) {
    const start = Math.max(0, end - 3);
    groups.unshift(integerDigits.slice(start, end));
  }

  return groups.join(thousandSeparator);
};

/**
 * Formats a whole number with UBM thousand separators and no decimals — for counts and round
 * amounts such as "14’900 searches / month" or "CHF 500 / month".
 *
 * Wraps `toStringNumberWithDelimiter` with the locale-aware separator, so callers do not have to
 * know the ASR-1648 mapping. That helper takes an optional number, hence the fallback; a defined
 * `value` always formats, including 0.
 */
export const formatIntegerWithThousandSeparator = (value: number, locale: string): string =>
  toStringNumberWithDelimiter(Math.round(value), getUbmThousandSeparator(locale)) ?? '0';

/**
 * Formats a numeric amount with exactly two decimals using UBM currency style
 * (ASR-1648). Does not include a currency prefix.
 */
export const formatNumberWithTwoDecimals = (value: number, locale: string): string => {
  const rounded = roundToTwoDecimals(value);
  const isNegative = rounded < 0;
  const [integerPart, fractionalPart] = Math.abs(rounded).toFixed(2).split('.');
  const groupedInteger = groupIntegerDigits(integerPart, getUbmThousandSeparator(locale));

  return `${isNegative ? '-' : ''}${groupedInteger}.${fractionalPart}`;
};
